import React, { useState } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';

// Complex number helpers
const cp = (mag, deg) => ({ re: mag * Math.cos((deg * Math.PI) / 180), im: mag * Math.sin((deg * Math.PI) / 180) });
const cadd = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmag = (z) => Math.sqrt(z.re * z.re + z.im * z.im);
const cang = (z) => (Math.atan2(z.im, z.re) * 180) / Math.PI;
const fmt  = (v, d = 4) => v.toFixed(d);

const DEFAULTS = {
  z1s_mag: 0.10, z1s_ang: 80,
  z0s_mag: 0.40, z0s_ang: 75,
  zt_mag:  0.08, zt_ang: 85,
  z1l_mag: 0.05, z1l_ang: 72,
  z0l_mag: 0.18, z0l_ang: 68,
};

const Row = ({ label, value, unit = 'pu', color = 'teal' }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">{label}</span>
    <span className={`text-xs font-bold font-mono text-${color}-700 dark:text-${color}-400`}>
      {value} <span className="text-slate-500 font-normal">{unit}</span>
    </span>
  </div>
);

const NumInput = ({ label, value, onChange, tooltip }) => (
  <TeachingTooltip {...tooltip}>
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wide">{label}</label>
      <input
        type="number" step="0.01" min="0" max="10"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
    </div>
  </TeachingTooltip>
);

const TheveninSeqCalc = () => {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((prev) => ({ ...prev, [k]: val }));

  // Build complex impedances
  const Z1s = cp(v.z1s_mag, v.z1s_ang);
  const Z0s = cp(v.z0s_mag, v.z0s_ang);
  const ZT  = cp(v.zt_mag,  v.zt_ang);
  const Z1l = cp(v.z1l_mag, v.z1l_ang);
  const Z0l = cp(v.z0l_mag, v.z0l_ang);

  // Thevenin impedances (simplified series model)
  const Z1_Th = cadd(cadd(Z1s, ZT), Z1l);
  const Z2_Th = Z1_Th; // Z2 ≈ Z1 for distribution systems
  const Z0_Th = cadd(cadd(Z0s, ZT), Z0l);

  return (
    <div data-testid="thevenin-seq-calc" className="space-y-5">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Compute the Thevenin sequence impedances at the fault bus. Uses a simplified two-element model:
        Z_Th = Z_source + Z_transformer + Z_line.
      </p>

      {/* Inputs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <NumInput label="|Z1s| pu" value={v.z1s_mag} onChange={set('z1s_mag')}
          tooltip={{ title: 'Positive-Sequence Source Z', whatThis: 'Thevenin impedance of the source (utility or generator) in the positive-sequence network (pu).', tryThis: 'Increase to model a weaker source (higher source impedance reduces fault current).', whyMatters: 'Dominates Z1_Th for faults close to the source bus.', connectedTo: 'Z1_Th result below' }} />
        <NumInput label="|Z0s| pu" value={v.z0s_mag} onChange={set('z0s_mag')}
          tooltip={{ title: 'Zero-Sequence Source Z', whatThis: 'Source impedance in the zero-sequence network. Typically 3–5× Z1s for overhead lines and generators.', tryThis: 'Set to 0 to model a solid infinite-bus ground (all SLG fault current returns through zero-impedance neutral).', whyMatters: 'Z0s >> Z1s means zero-sequence limits SLG fault current more than positive sequence.', connectedTo: 'Z0_Th result below' }} />
        <NumInput label="|Z_T| pu" value={v.zt_mag} onChange={set('zt_mag')}
          tooltip={{ title: 'Transformer Impedance', whatThis: 'Transformer leakage reactance in per-unit. Typical values: 0.05–0.12 pu for distribution transformers.', tryThis: 'Match this to the transformer nameplate %Z value (divide by 100).', whyMatters: 'Z_T appears in all three sequence networks for Wye-Wye connections. For Dyn, Z_T appears in zero-sequence but not from the source side.', connectedTo: 'Z1_Th, Z2_Th, Z0_Th results' }} />
        <NumInput label="|Z1_line| pu" value={v.z1l_mag} onChange={set('z1l_mag')}
          tooltip={{ title: 'Positive-Sequence Line Z', whatThis: 'Feeder line impedance from the transformer to the fault bus in the positive-sequence domain (pu).', tryThis: 'Increase to model a fault farther from the substation — this reduces fault current magnitude.', whyMatters: 'Z1_line sets the positive-sequence impedance between the transformer bus and the fault.', connectedTo: 'Z1_Th, Z2_Th results' }} />
        <NumInput label="|Z0_line| pu" value={v.z0l_mag} onChange={set('z0l_mag')}
          tooltip={{ title: 'Zero-Sequence Line Z', whatThis: 'Feeder line impedance in the zero-sequence domain. For overhead lines with ground wires, Z0_line ≈ 3×Z1_line.', tryThis: 'Set to 3× Z1_line to model a typical overhead feeder. Set to 1× Z1_line to model a cable.', whyMatters: 'Z0_line >> Z1_line for overhead lines — this is why Z0_Th >> Z1_Th and SLG fault current is lower than 3-phase fault current.', connectedTo: 'Z0_Th result below' }} />
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Z\u2081_Th (Positive)', z: Z1_Th, color: 'teal', desc: '= Z\u2081s + Z_T + Z\u2081_line' },
          { label: 'Z\u2082_Th (Negative)', z: Z2_Th, color: 'orange', desc: '\u2248 Z\u2081_Th (static elements)' },
          { label: 'Z\u2080_Th (Zero)',     z: Z0_Th, color: 'purple', desc: '= Z\u2080s + Z_T + Z\u2080_line' },
        ].map(({ label, z, color, desc }) => (
          <div key={label} className={`rounded-lg border p-4 bg-${color === 'teal' ? 'teal' : color === 'orange' ? 'orange' : 'purple'}-50/50 dark:bg-${color === 'teal' ? 'teal' : color === 'orange' ? 'orange' : 'purple'}-950/20 border-${color === 'teal' ? 'teal' : color === 'orange' ? 'orange' : 'purple'}-200 dark:border-${color === 'teal' ? 'teal' : color === 'orange' ? 'orange' : 'purple'}-800`}>
            <p className="text-xs font-bold font-mono mb-1" style={{ color: color === 'teal' ? '#0d9488' : color === 'orange' ? '#ea580c' : '#7c3aed' }}>{label}</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-500 mb-3">{desc}</p>
            <Row label="Magnitude" value={`${fmt(cmag(z), 4)}`} color={color} />
            <Row label="Angle"     value={`${fmt(cang(z), 1)}\u00b0`} unit="" color={color} />
            <Row label="R + jX"    value={`${fmt(z.re, 4)} + j${fmt(z.im, 4)}`} unit="" color={color} />
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 dark:text-slate-600">
        Assumption: Z2 \u2248 Z1 for distribution systems (no large rotating machines near fault bus). Transformer modeled as series impedance in all three sequence networks.
      </p>
    </div>
  );
};

export default TheveninSeqCalc;
